/**
 * "My work" — `/my-work`, a shell route beside the Cockpit (bata-ui#166 W0-8, DEC-01, bata-ui ADR
 * 9022). For the signed-in person: the queues where they can act now, with how much waits in each,
 * and the queues their roles do not grant, each with the platform's reason.
 *
 * The rules — which read each count comes from, what is never counted — are in `./my-work.ts`.
 * Every read here is a read another screen already makes with the same parameters (the run
 * directory, the decision summary, the price run, the pending approvals, the execution pack), so
 * the counts are the same cache entries the queues themselves render and cannot disagree with them.
 *
 * **The designer's template S (brief part 1 §5 screen 06, Package C6).** The sentence (how many
 * things wait), four figures, the work by age, ONE table — Cosa · App · waiting · Da quando · Valore
 * — whose whole row opens the queue (`onNavigate`, and the row's «Open» for the keyboard), the run
 * the counts are of as constants, the reads as provenance, inside the page's ONE strip (ADR 9033).
 * The queues not in the person's role are rows of the same table with the platform's reason and no
 * link. No read publishes how old an item is or what it is worth, so the age bars, «Da quando»,
 * «Valore» and the oldest item's age are «In arrivo», said once in the strip; the designer's «the
 * oldest from 3 days» clause is not drawn.
 */

import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';

import { useApiCollection, useApiQuery } from '@/api';
import { decisionSummary, executionPack, inseasonListRuns, listChanges } from '@/api/generated';
import type { UseQueryResult } from '@tanstack/react-query';
import { judgeCapability, useMyCapabilities } from '@/auth/capabilities';
import { WhyNot } from '@/auth/capability-gate';
import { packLines, packTally } from '@/features/inseason/execution-pack';
import { isNotPublished } from '@/features/inseason/not-published';
import { MOVE_RUN_ID_KEY } from '@/features/inseason/pending-links';
import { formatInteger } from '@/lib/format';
import {
  ArrivingChart, DataTable, PageStatusRegion, RunWords, ScreenTemplate, arrivingColumn, usePageStatusEntry,
  type ConstantPair, type DataColumn, type TemplateKpi, type TemplateSentence,
} from '@/planning';
import { Button, Skeleton, StatusIndicator } from '@/primitives';

import { RAIL_APPS } from '../../chrome/rail-layout';
import { priceRunIdOf, resolveRunId, RUN_ID_PARAM } from '../overview/scope';
import { resolvePriceRun } from '../overview/price-run';

import {
  QUEUE_SPEC, WORK_QUEUE_IDS, alertsOverdue, awaitingOthers, queueApp, queueCounts, workPlan,
  type QueueCount, type Read, type WorkQueue, type WorkQueueId,
} from './my-work';
import { useWorkCounts } from './work-counts';

/** The Cockpit's own directory request (`OverviewScreen`'s limit), so both read one cache entry. */
const RUN_DIRECTORY_LIMIT = 10;

function readOf<T, U = T>(query: UseQueryResult<T, Error>, map?: (data: T) => U): Read<U> {
  if (query.data !== undefined) return { kind: 'ready', data: map === undefined ? (query.data as unknown as U) : map(query.data) };
  if (query.isError) return { kind: 'failed', notPublished: isNotPublished(query.error) };
  return { kind: 'loading' };
}

function Count({ count }: { readonly count: QueueCount }): React.JSX.Element {
  const { t } = useTranslation();
  if (count.kind === 'loading') return <Skeleton rows={1} />;
  if (count.kind === 'absent') {
    return <span className="text-caption text-ink-secondary">{t(count.reasonKey)}</span>;
  }
  return count.value === 0
    ? <span className="text-ink-secondary">{t('myWork.count.none')}</span>
    : <StatusIndicator level="warning" labelKey={count.groups === true ? 'myWork.count.waitingGroups' : 'myWork.count.waiting'} values={{ count: count.value }} />;
}

/** The sentence over the queues this person may act on. `null` while a count is still being read. Pure, for the test. */
export function myWorkSentence(mine: readonly WorkQueue[]): TemplateSentence | null {
  if (mine.some((queue) => queue.count.kind === 'loading')) return null;
  const count = mine.reduce((sum, queue) => sum + (queue.count.kind === 'count' ? queue.count.value : 0), 0);
  const uncounted = mine.filter((queue) => queue.count.kind === 'absent').length;
  return { key: 'myWork.sentence', values: { count, uncounted } };
}

/** A row of the table: a queue, and whether it is this person's to open. */
interface WorkRow {
  readonly queue: WorkQueue;
  readonly mine: boolean;
}

export interface MyWorkScreenProps {
  readonly onNavigate: (to: string, params?: Record<string, string>) => void;
}

export function MyWorkScreen({ onNavigate }: MyWorkScreenProps): React.JSX.Element {
  return (
    <PageStatusRegion>
      <MyWork onNavigate={onNavigate} />
    </PageStatusRegion>
  );
}

function MyWork({ onNavigate }: MyWorkScreenProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const capabilities = useMyCapabilities();

  const directory = useApiQuery(inseasonListRuns, { query: { limit: RUN_DIRECTORY_LIMIT } });
  const runs = directory.data?.items ?? [];
  const runId = directory.data === undefined ? null : resolveRunId(null, runs);
  const run = runs.find((row) => row.run_id === runId);
  // The price steps live on the newest run that has any (the Cockpit's F16 rule, one function).
  const priceRunId = priceRunIdOf(resolvePriceRun(runId, {
    ready: directory.data !== undefined,
    failed: directory.isError,
    runs,
  }));

  const summary = useApiQuery(decisionSummary, { path: { run_id: runId ?? '' } }, { enabled: runId !== null });
  const priceOnOtherRun = priceRunId !== null && priceRunId !== runId;
  const priceSummary = useApiQuery(decisionSummary, { path: { run_id: priceRunId ?? '' } }, { enabled: priceOnOtherRun });
  // The pending-approvals panel's own request, so the count is that queue's rows.
  const pending = useApiCollection(listChanges, { query: { state: 'proposed' } });
  const pack = useApiQuery(executionPack, { path: { run_id: runId ?? '' }, query: {} }, { enabled: runId !== null });
  // `/me/work-counts` (W3-4): the same read, and cache entry, as the rail's badge.
  const workCounts = readOf(useWorkCounts());

  const noRun = directory.data !== undefined && runId === null;
  const directoryFailed = directory.isError;
  const runBound = <T,>(read: Read<T>): Read<T> | null => (noRun ? null : directoryFailed ? { kind: 'failed', notPublished: false } : read);

  const counts = queueCounts({
    summary: runBound(runId === null ? { kind: 'loading' } : readOf(summary)),
    priceSummary: runBound(priceOnOtherRun ? readOf(priceSummary) : runId === null ? { kind: 'loading' } : readOf(summary)),
    pendingChanges: readOf(pending),
    pack: runBound(runId === null ? { kind: 'loading' } : readOf(pack, (data) => packTally(packLines(data)))),
    workCounts,
  });
  const overdue = alertsOverdue(workCounts);
  const answer = capabilities.state === 'answered' ? capabilities.data : undefined;
  const verdicts = Object.fromEntries(WORK_QUEUE_IDS.map((id) => [
    id, judgeCapability(answer, QUEUE_SPEC[id].capability.kind, QUEUE_SPEC[id].capability.action, { country: run?.country_key ?? null }),
  ])) as Record<WorkQueueId, ReturnType<typeof judgeCapability>>;
  const plan = workPlan(verdicts, counts);
  const othersToApprove = pending.data === undefined ? 0 : awaitingOthers(pending.data);

  // The page's states, said once in its strip (ADR 9033): what is not published, and permissions
  // that could not be read or are not published — every queue is then listed and still checked.
  usePageStatusEntry({ kind: 'not-published', message: { key: 'myWork.notPublished' } });
  usePageStatusEntry(capabilities.state === 'unavailable'
    ? { kind: 'error', message: { key: 'myWork.capabilities.unavailable' } }
    : capabilities.state === 'notPublished'
      ? { kind: 'not-published', message: { key: 'myWork.capabilities.notPublished' } }
      : null);

  const open = (queue: WorkQueue): void => {
    // The queue opens on the run its count is of: the price run for price steps.
    if (queue.id === 'transfers') {
      // The moves the count is of: the one run's list, on the move run the summary names (platform v0.126.0).
      const moveRunId = summary.data?.transfers_pending_move_run_id ?? null;
      onNavigate(queue.spec.path, moveRunId === null ? {} : { [MOVE_RUN_ID_KEY]: moveRunId });
      return;
    }
    const target = !queue.spec.runBound ? null : queue.id === 'priceSteps' ? priceRunId : runId;
    onNavigate(queue.spec.path, { ...(queue.spec.params ?? {}), ...(target === null ? {} : { [RUN_ID_PARAM]: target }) });
  };

  const waiting = plan.mine.reduce((sum, queue) => sum + (queue.count.kind === 'count' ? queue.count.value : 0), 0);
  const withWork = plan.mine.filter((queue) => queue.count.kind === 'count' && queue.count.value > 0).length;
  const kpis: readonly TemplateKpi[] = [
    {
      id: 'waiting', labelKey: 'myWork.kpi.waiting', descriptionKey: 'help.myWorkScreen.waiting', value: formatInteger(waiting, locale),
      context: t('myWork.kpi.inQueues', { queues: withWork }),
    },
    {
      id: 'queues', labelKey: 'myWork.kpi.queues', descriptionKey: 'help.myWorkScreen.queues', value: formatInteger(plan.mine.length, locale),
      context: t('myWork.kpi.notMine', { count: plan.notMine.length }),
    },
    {
      id: 'overdue', labelKey: 'myWork.kpi.overdue', descriptionKey: 'help.myWorkScreen.overdue', value: overdue === null ? null : formatInteger(overdue, locale),
      ...(overdue !== null && overdue > 0 ? { alert: { key: 'myWork.alerts.overdue', values: { count: overdue } } } : {}),
    },
    { id: 'oldest', labelKey: 'myWork.kpi.oldest', descriptionKey: 'help.myWorkScreen.oldest', value: null, arriving: true },
  ];

  const constants: readonly ConstantPair[] = run === undefined
    ? [{ id: 'run', labelKey: 'myWork.constant.run', value: t(noRun ? 'myWork.constant.noRun' : 'myWork.constant.reading') }]
    : [
      { id: 'run', labelKey: 'myWork.constant.run', value: <RunWords runId={run.run_id} /> },
      { id: 'market', labelKey: 'myWork.constant.market', value: run.country_key, mono: true },
      ...(priceOnOtherRun && priceRunId !== null
        ? [{ id: 'price-run', labelKey: 'myWork.constant.priceRun', value: <RunWords runId={priceRunId} /> }]
        : []),
    ];

  const rows: readonly WorkRow[] = [
    ...plan.mine.map((queue) => ({ queue, mine: true })),
    ...plan.notMine.map((queue) => ({ queue, mine: false })),
  ];
  const columns: readonly DataColumn<WorkRow>[] = [
    {
      id: 'what', headerKey: 'myWork.column.what', descriptionKey: 'help.myWorkScreen.what', kind: 'text',
      value: (row) => t(row.queue.spec.labelKey),
      cell: (row) => (
        <th scope="row" className={`py-1 pe-4 text-start ${row.mine ? 'text-ink-primary font-semibold' : 'text-ink-secondary font-normal'}`}>
          {/* Wrapped, not cut (usability audit D5): a bare string here is drawn by the table on
              one line at 20rem, which cut «Righe esportate da confermare come eseguite». The
              queue's name is the row's whole point, so it takes two lines rather than lose words. */}
          <span className="block max-w-[20rem] [overflow-wrap:anywhere]" data-queue-name="">{t(row.queue.spec.labelKey)}</span>
        </th>
      ),
    },
    {
      id: 'app', headerKey: 'myWork.column.app', descriptionKey: 'help.myWorkScreen.app', kind: 'enum',
      value: (row) => queueApp(row.queue.spec.path)?.id ?? null,
      label: (id) => t(RAIL_APPS.find((app) => app.id === id)?.labelKey ?? id),
      cell: (row) => {
        const app = queueApp(row.queue.spec.path);
        return <td className="py-1 pe-4">{app === null ? t('absent.notStated') : t(app.labelKey)}</td>;
      },
    },
    {
      // What matters: how much waits — or, for a queue not in the role, why it is not yours.
      id: 'waiting', headerKey: 'myWork.column.waiting', descriptionKey: 'help.myWorkScreen.waitingCol', kind: 'number', align: 'start', emphasis: true,
      value: (row) => (row.mine && row.queue.count.kind === 'count' ? row.queue.count.value : null),
      cell: (row) => (
        <td className="py-1 pe-4">
          {row.mine ? (
            <span className="flex flex-col gap-1">
              <Count count={row.queue.count} />
              {row.queue.id === 'alertsMine' && overdue !== null && overdue > 0 && (
                <StatusIndicator level="critical" labelKey="myWork.alerts.overdue" values={{ count: overdue }} />
              )}
              {row.queue.id === 'approvals' && othersToApprove > 0 && (
                <span className="text-caption text-ink-secondary">{t('myWork.approvals.awaitingOthers', { count: othersToApprove })}</span>
              )}
            </span>
          ) : row.queue.verdict.state === 'denied' ? <WhyNot verdict={row.queue.verdict} /> : null}
        </td>
      ),
    },
    arrivingColumn<WorkRow>({ id: 'since', headerKey: 'myWork.column.since', descriptionKey: 'help.myWorkScreen.since', align: 'start' }),
    arrivingColumn<WorkRow>({ id: 'value', headerKey: 'myWork.column.value', descriptionKey: 'help.myWorkScreen.value' }),
    {
      id: 'open', headerKey: 'myWork.column.open', descriptionKey: 'help.myWorkScreen.open',
      cell: (row) => (
        <td className="py-1 text-end">
          {row.mine && (
            <Button
              variant="secondary"
              size="sm"
              icon={<ArrowRight size={14} aria-hidden />}
              // The row opens the queue too; the button is the keyboard's way there, and says it once.
              onClick={(event) => { event.stopPropagation(); open(row.queue); }}
            >
              {t('myWork.open')}
            </Button>
          )}
        </td>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <ScreenTemplate
        sentence={myWorkSentence(plan.mine)}
        kpis={kpis}
        visual={<ArrivingChart descriptionKey="help.myWorkScreen.chart" titleKey="myWork.chart.title" height={160} />}
        constants={constants}
        table={(
          <DataTable sourceKey="help.myWorkScreen.source"
            density
            rows={rows}
            rowKey={(row) => row.queue.id}
            captionKey="myWork.caption"
            exportId="myWork.table"
            exportTitleKey="myWork.title"
            rowProps={(row) => ({
              'data-queue': row.queue.id,
              'data-mine': row.mine ? 'true' : 'false',
              className: `border-hairline border-b ${row.mine ? 'hover:bg-surface-sunken cursor-pointer' : ''}`,
              ...(row.mine ? { onClick: () => open(row.queue) } : {}),
            } as React.HTMLAttributes<HTMLTableRowElement>)}
            columns={columns}
          />
        )}
        provenance={['me/work-counts', ...(run === undefined ? [] : [run.run_id])]}
      />
      {answer?.registerProvisional === true && (
        <p className="text-caption text-ink-secondary max-w-prose">{t('myWork.provisional')}</p>
      )}
      <p className="text-caption text-ink-secondary max-w-prose">{t('myWork.notCounted')}</p>
    </div>
  );
}
